import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { metaForPath, canonicalFor, SITE } from "@/seo/meta";

// Keeps the document head in step with client-side navigation.
//
// scripts/prerender.cjs already writes the correct tags into each route's HTML
// file, which is what crawlers and link unfurlers read. This component covers
// the other half: once React takes over, moving between routes never reloads
// the document, so without it the head would keep the first page's title for
// the rest of the visit — wrong in the tab, in bookmarks, and in history.
//
// It rewrites the tags that are already in public/index.html rather than adding
// its own, so a page never ends up with two descriptions for a crawler to pick
// between.

function setMeta(selector, attr, value, head) {
  let el = head.querySelector(selector);
  if (!el) {
    const [, kind, name] = selector.match(/^meta\[(name|property)="(.+)"\]$/) || [];
    if (!kind) return;
    el = document.createElement("meta");
    el.setAttribute(kind, name);
    head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

export default function RouteSeo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = metaForPath(pathname);
    const url = canonicalFor(pathname);
    const head = document.head;

    document.title = meta.title;

    setMeta('meta[name="description"]', "content", meta.description, head);
    setMeta('meta[property="og:title"]', "content", meta.title, head);
    setMeta('meta[property="og:description"]', "content", meta.description, head);
    setMeta('meta[property="og:url"]', "content", url, head);
    setMeta('meta[property="og:type"]', "content", meta.type || "website", head);
    setMeta('meta[name="twitter:title"]', "content", meta.title, head);
    setMeta('meta[name="twitter:description"]', "content", meta.description, head);
    setMeta('meta[property="og:image"]', "content", meta.image || SITE.image, head);
    setMeta('meta[name="twitter:image"]', "content", meta.image || SITE.image, head);

    let canonical = head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);
  }, [pathname]);

  return null;
}
